import { Button, TextField, TextFieldProps } from '@mui/material';
import Required from 'components/commons/Required';
import Text from 'components/commons/Text';
import Row from 'components/layouts/Row';
import { useLocale } from 'providers/LocalProvider';
import { translateText } from 'libs/helper/translationHelper';

import React, {
  CSSProperties,
  forwardRef,
  useRef,
  useImperativeHandle,
  useState,
  useEffect,
} from 'react';
import { AdminSystemLocaleDto, SystemLocaleDto } from 'services/data-contracts';
import RichEditor from 'components/editor/RichEditor';

interface InputFieldProps extends Omit<TextFieldProps, 'onChange'> {
  label?: string;
  value?: Record<string, string>;
  style?: CSSProperties;
  required?: boolean;
  defaultValue?: Record<string, string>;
  useTranslation?: boolean;
  onChange?: (value: Record<string, string>) => void;
}

const LocalizeEditor = forwardRef(
  (
    {
      label,
      style,
      required,
      defaultValue,
      onChange,
      helperText,
      useTranslation,
      value,
      ...props
    }: InputFieldProps,
    ref: any,
  ) => {
    const inputRefs = useRef<Record<string, HTMLInputElement>>({});
    const isFirstRender = useRef(true);
    const valueRef = useRef<Record<string, string>>({});

    const { activeLocale } = useLocale();

    const [localValue, setLocalValue] = useState<Record<string, string>>(
      value || defaultValue || {},
    );

    useImperativeHandle(ref, () => ({
      focus,
      getValue: () => localValue,
      setValue: (newValue: Record<string, string>) => {
        setLocalValue(newValue);
      },
    }));

    const focus = () => {
      const firstLocale = activeLocale?.[0]?.locale;
      if (firstLocale) {
        inputRefs.current[firstLocale]?.focus();
      }
    };

    const handleTranslation = async (sourceText: string) => {
      if (!sourceText) return;

      const translations: Record<string, string> = { ...localValue };

      const translationPromises = activeLocale
        ?.filter((lang) => lang.locale !== 'ko')
        ?.map(async (lang) => {
          const locale = lang.locale || '';
          const translatedText = await translateText(sourceText, locale);
          if (translatedText) {
            translations[locale] = translatedText;
          }
        });

      await Promise.all(translationPromises ?? []);
      setLocalValue(translations);
      onChange?.(translations);
    };

    useEffect(() => {
      if (value) {
        setLocalValue(value);
        valueRef.current = value;
      }
    }, [value]);

    useEffect(() => {
      isFirstRender.current = false;
    }, []);


    return (
      <>
        {useTranslation && (
          <Button onClick={() => handleTranslation(localValue['ko'] ?? '')}>번역</Button>
        )}
        <Row style={{ width: '100%', flex: 1, flexDirection: 'column', gap: 10, ...style }}>
          {activeLocale?.map((language: SystemLocaleDto) => (
            <React.Fragment key={language.locale}>
              <Row style={{ justifyContent: 'flex-start' }}>
                <Text type="H4">
                  {language.name} ({language.locale})
                </Text>
                <>{required && <Required />}</>
              </Row>
              <RichEditor
                defaultValue={defaultValue?.[language.locale ?? ''] || ''}
                onEditorChange={(htmlValue: string) => {
                  const newValue = {
                    ...valueRef.current,
                    [language.locale ?? '']: htmlValue,
                  };

                  valueRef.current = newValue;
                  setLocalValue(newValue);
                  onChange?.(newValue);
                }}
              />
            </React.Fragment>
          ))}
        </Row>
      </>
    );
  },
);

export default LocalizeEditor;
